// ============================================
// pages/Notifications.tsx
// 独立消息中心页（路由 /notifications）
//   - 拉全部个人通知，完整列表
//   - 点某条 → 进详情页
//   - 单条删除（垃圾桶）+ 顶部清空（软删除：客户端删、后端保留）
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ArrowLeft, Trash2 } from 'lucide-react';
import { getToken } from '../services/api';
import Layout from '../components/Layout';

const PUSH_BASE = 'https://push.hipiido.cloud';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  payload: Record<string, unknown>;
  read: boolean;
  created_at: number;
}

export default function Notifications() {
  const navigate = useNavigate();
  const [list, setList] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmClear, setConfirmClear] = useState(false);

  const fetchList = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const resp = await fetch(`${PUSH_BASE}/notifications/list?token=${encodeURIComponent(token)}`);
      const data = await resp.json();
      if (data && data.ok) setList(data.list || []);
    } catch (e) { /* 拉取失败显示空 */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  // 点某条 → 进详情页（带整条数据）
  const onClickItem = (n: NotificationItem) => {
    navigate(`/notifications/${encodeURIComponent(n.id)}`, { state: { notification: n } });
  };

  // 删除单条（软删除：本地先移除，后端打隐藏标记）
  const onDeleteOne = async (e: React.MouseEvent, n: NotificationItem) => {
    e.stopPropagation();   // 不要触发进详情
    setList((prev) => prev.filter((x) => x.id !== n.id));   // 本地即时移除
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${PUSH_BASE}/notifications/hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, notification_id: n.id }),
      });
    } catch (err) { /* 失败也已本地移除；下次刷新会以后端为准 */ }
  };

  // 清空（软删除全部）
  const onClearAll = async () => {
    setConfirmClear(false);
    setList([]);   // 本地即时清空
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${PUSH_BASE}/notifications/hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),   // 不带 notification_id = 清空全部
      });
    } catch (err) { /* 失败也已本地清空 */ }
  };

  if (!getToken()) {
    return (
      <Layout width="narrow">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center text-ink-400">
          请先登录后查看消息
        </div>
      </Layout>
    );
  }

  return (
    <Layout width="narrow">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* 顶部标题栏 */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate('/')}
            className="grid place-items-center w-9 h-9 rounded-full hover:bg-ink-100 transition-colors"
            aria-label="返回"
          >
            <ArrowLeft className="w-5 h-5 text-ink-600" />
          </button>
          <h1 className="text-xl font-bold text-ink-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-gold-500" strokeWidth={2} />
            我的消息
          </h1>
          {/* 清空按钮（有消息才显示） */}
          {list.length > 0 && (
            <button
              onClick={() => setConfirmClear(true)}
              className="ml-auto text-sm text-ink-400 hover:text-red-500 transition-colors"
            >
              清空
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-ink-400">加载中…</div>
        ) : list.length === 0 ? (
          <div className="py-16 text-center text-sm text-ink-400">还没有消息</div>
        ) : (
          <div className="bg-white rounded-2xl shadow-card border border-ink-300/40 overflow-hidden">
            {list.map((n) => (
              <div
                key={n.id}
                className={`group flex items-stretch border-b border-ink-300/20 last:border-b-0 ${n.read ? '' : 'bg-gold-50'}`}
              >
                {/* 点击区：进详情 */}
                <button
                  onClick={() => onClickItem(n)}
                  className="flex-1 text-left px-5 py-4 hover:bg-paper transition-colors min-w-0"
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-red-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink-900 leading-snug">{n.title}</div>
                      {n.payload && n.payload.content_preview ? (
                        <div className="text-xs text-ink-500 mt-1 break-words">
                          {String(n.payload.content_preview)}
                        </div>
                      ) : null}
                      <div className="text-[11px] text-ink-400 mt-1.5">{formatTime(n.created_at)}</div>
                    </div>
                  </div>
                </button>
                {/* 删除按钮 */}
                <button
                  onClick={(e) => onDeleteOne(e, n)}
                  className="px-4 grid place-items-center text-ink-300 hover:text-red-500 transition-colors"
                  aria-label="删除"
                  title="删除"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 清空确认弹窗 */}
      {confirmClear && (
        <div
          className="fixed inset-0 z-[60] grid place-items-center bg-black/30"
          onClick={() => setConfirmClear(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-card max-w-xs w-[88%] p-5 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-bold text-ink-900 mb-2">清空全部消息？</div>
            <div className="text-sm text-ink-500 leading-relaxed mb-5">清空后将不再显示这些消息</div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmClear(false)}
                className="flex-1 py-2 rounded-full border border-ink-300/50 text-sm text-ink-600 hover:bg-paper transition-colors"
              >
                取消
              </button>
              <button
                onClick={onClearAll}
                className="flex-1 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
              >
                清空
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function formatTime(ts: number): string {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  return new Date(ts).toLocaleDateString('zh-CN');
}
